import React from 'react';
import {
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  CartesianGrid
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { STATUS } from '../lib/theme';

// Color palette untuk charts, diselaraskan dengan STATUS di lib/theme.js
const COLORS = {
  valid: STATUS.valid.icon,
  warning: STATUS.warning.icon,
  critical: STATUS.critical.icon,
  expired: STATUS.expired.icon,
};

// Mini Sparkline Chart untuk Stats Card
export function MiniSparkline({ data, color = '#3b82f6', height = 40 }) {
  if (!data || data.length === 0) return null;

  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data}>
        <Line
          type="monotone"
          dataKey="value"
          stroke={color}
          strokeWidth={2}
          dot={false}
          animationDuration={1000}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

// Certificate Status Distribution Pie Chart
export function StatusPieChart({ data }) {
  if (!data || data.length === 0) return null;

  const chartData = [
    { name: 'Valid', value: data.valid || 0, color: COLORS.valid },
    { name: 'Akan Kedaluwarsa', value: data.warning || 0, color: COLORS.warning },
    { name: 'Kritis', value: data.critical || 0, color: COLORS.critical },
    { name: 'Kedaluwarsa', value: data.expired || 0, color: COLORS.expired },
  ].filter(item => item.value > 0);

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="flex-shrink-0">
        <CardTitle className="text-base sm:text-lg">Distribusi Status</CardTitle>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col justify-center">
        <ResponsiveContainer width="100%" height={200}>
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius={50}
              outerRadius={80}
              paddingAngle={5}
              dataKey="value"
              animationDuration={1000}
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{
                background: 'white',
                border: '1px solid #e8ecf2',
                borderRadius: '4px',
                padding: '8px 12px'
              }}
            />
          </PieChart>
        </ResponsiveContainer>
        <div className="grid grid-cols-2 gap-2 mt-4">
          {chartData.map((entry, index) => (
            <div key={index} className="flex items-center gap-2 text-xs">
              <div
                className="w-2.5 h-2.5 rounded-sm"
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-muted-foreground">{entry.name}:</span>
              <span className="font-semibold">{entry.value}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// Certificate Expiry Timeline Chart
export function ExpiryTimelineChart({ certificates }) {
  if (!certificates || certificates.length === 0) return null;

  // Group certificates by expiry timeframe
  const now = new Date();
  const timeframes = [
    { label: '0-7 hari', days: 7, count: 0 },
    { label: '8-30 hari', days: 30, count: 0 },
    { label: '31-60 hari', days: 60, count: 0 },
    { label: '61-90 hari', days: 90, count: 0 },
    { label: '>90 hari', days: 999, count: 0 },
  ];

  certificates.forEach(cert => {
    const expiryDate = new Date(cert.expiration_date);
    const daysUntilExpiry = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));

    if (daysUntilExpiry <= 7) timeframes[0].count++;
    else if (daysUntilExpiry <= 30) timeframes[1].count++;
    else if (daysUntilExpiry <= 60) timeframes[2].count++;
    else if (daysUntilExpiry <= 90) timeframes[3].count++;
    else timeframes[4].count++;
  });

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="flex-shrink-0">
        <CardTitle className="text-base sm:text-lg">Timeline Kedaluwarsa</CardTitle>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col justify-center">
        <div className="space-y-3">
          {timeframes.map((timeframe, index) => {
            const percentage = certificates.length > 0
              ? (timeframe.count / certificates.length) * 100
              : 0;

            const barColor =
              timeframe.days <= 7 ? STATUS.critical.icon :
                timeframe.days <= 30 ? STATUS.warning.icon :
                  timeframe.days <= 60 ? STATUS.warning.icon :
                    STATUS.valid.icon;

            return (
              <div key={index}>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="text-muted-foreground">{timeframe.label}</span>
                  <span className="font-semibold">{timeframe.count} sertifikat</span>
                </div>
                <div className="w-full bg-muted rounded-sm h-1.5 overflow-hidden">
                  <div
                    className="h-full transition-all duration-1000 ease-out"
                    style={{ width: `${percentage}%`, background: barColor }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

// Trend/Proyeksi Chart - dipakai untuk proyeksi kedaluwarsa ke depan
export function CertificateTrendChart({ data, title = 'Trend Sertifikat', totalLabel = 'Total', expiringLabel = 'Akan Kedaluwarsa' }) {
  if (!data || data.length === 0) return null;

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="flex-shrink-0">
        <CardTitle className="text-base sm:text-lg">{title}</CardTitle>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col justify-center">
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={data} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e8ecf2" vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11, fill: '#8892a4' }}
              axisLine={{ stroke: '#e8ecf2' }}
              tickLine={false}
            />
            <Line
              type="monotone"
              dataKey="total"
              stroke={STATUS.primary.icon}
              strokeWidth={2}
              dot={{ fill: STATUS.primary.icon, r: 3 }}
              animationDuration={1500}
            />
            <Line
              type="monotone"
              dataKey="expiring"
              stroke={STATUS.warning.icon}
              strokeWidth={2}
              dot={{ fill: STATUS.warning.icon, r: 3 }}
              animationDuration={1500}
            />
            <Tooltip
              contentStyle={{
                background: 'white',
                border: '1px solid #e8ecf2',
                borderRadius: '4px',
                padding: '8px 12px',
                fontSize: '12px'
              }}
            />
          </LineChart>
        </ResponsiveContainer>
        <div className="flex items-center justify-center gap-6 mt-2 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-2.5 h-2.5 rounded-sm" style={{ background: STATUS.primary.icon }} />
            <span className="text-muted-foreground">{totalLabel}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-2.5 h-2.5 rounded-sm" style={{ background: STATUS.warning.icon }} />
            <span className="text-muted-foreground">{expiringLabel}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
